import React, { useEffect, useState, useRef } from "react";

import "../Styles/gallery.css";

const API_BASE_URL = "";

const VnkGallery = () => {
  // ------------------------------------------------------------
  // Gallery data from backend
  // ------------------------------------------------------------
  const [galleryEvents, setGalleryEvents] = useState([]);

  const [images, setImages] = useState([]);

  const [categoriesList, setCategoriesList] = useState([
    {
      key: "all",
      label: "All",
    },
  ]);

  const [selectedCategory, setSelectedCategory] = useState("all");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------
  // Modal states
  // ------------------------------------------------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const modalImageRef = useRef(null);
  const modalContentRef = useRef(null);

  // ------------------------------------------------------------
  // Fetch gallery events from backend
  // ------------------------------------------------------------
  useEffect(() => {
    const fetchGalleryEvents = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(
          `${API_BASE_URL}/api/gallery-events`
        );

        if (!res.ok) {
          throw new Error("Failed to load gallery events");
        }

        const data = await res.json();

        // Save complete backend data
        setGalleryEvents(Array.isArray(data) ? data : []);

        // --------------------------------------------------------
        // Build dropdown categories from database events
        // --------------------------------------------------------
        const categories = [
          {
            key: "all",
            label: "All",
          },
          ...(Array.isArray(data)
            ? data.map((event) => ({
                key: String(event.id),
                label: event.eventName,
              }))
            : []),
        ];

        setCategoriesList(categories);
      } catch (err) {
        console.error("Gallery fetch error:", err);

        setGalleryEvents([]);
        setCategoriesList([
          {
            key: "all",
            label: "All",
          },
        ]);

        setError("Failed to load gallery. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryEvents();
  }, []);

  // ------------------------------------------------------------
  // Build images whenever category changes
  // ------------------------------------------------------------
  useEffect(() => {
    if (!galleryEvents || galleryEvents.length === 0) {
      setImages([]);
      return;
    }

    // ------------------------------------------------------------
    // Flatten every image from every event into a single list.
    // Each event now has an `images` array (from galleryimages table)
    // instead of a single `imageUrl`.
    // ------------------------------------------------------------
    const allImages = galleryEvents.flatMap((event) =>
      (event.images || []).map((img) => ({
        id: img.id,
        image_url: img.imageUrl,
        galleryName: event.eventName,
        category: String(event.id),
      }))
    );

    // ------------------------------------------------------------
    // ALL
    // ------------------------------------------------------------
    if (selectedCategory === "all") {
      setImages(allImages);
      return;
    }

    // ------------------------------------------------------------
    // PARTICULAR EVENT — show every image belonging to that event
    // ------------------------------------------------------------
    const eventImages = allImages.filter(
      (img) => img.category === String(selectedCategory)
    );

    setImages(eventImages);
  }, [selectedCategory, galleryEvents]);

  // ------------------------------------------------------------
  // Reset modal index when images change
  // ------------------------------------------------------------
  useEffect(() => {
    setCurrentIndex(0);
    setZoomed(false);
  }, [selectedCategory, images.length]);

  // ------------------------------------------------------------
  // Open modal
  // ------------------------------------------------------------
  const openVnkModal = (idx) => {
    setCurrentIndex(idx);
    setModalOpen(true);
    setZoomed(false);
  };

  // ------------------------------------------------------------
  // Close modal
  // ------------------------------------------------------------
  const closeVnkModal = () => {
    setModalOpen(false);
    setZoomed(false);
  };

  // ------------------------------------------------------------
  // Previous image
  // ------------------------------------------------------------
  const showVnkPrev = (e) => {
    if (e) {
      e.stopPropagation();
    }

    if (images.length === 0) return;

    setCurrentIndex((prev) =>
      prev > 0 ? prev - 1 : images.length - 1
    );

    setZoomed(false);
  };

  // ------------------------------------------------------------
  // Next image
  // ------------------------------------------------------------
  const showVnkNext = (e) => {
    if (e) {
      e.stopPropagation();
    }

    if (images.length === 0) return;

    setCurrentIndex((prev) =>
      prev < images.length - 1 ? prev + 1 : 0
    );

    setZoomed(false);
  };

  // ------------------------------------------------------------
  // Fullscreen
  // ------------------------------------------------------------
  const handleVnkFullscreen = () => {
    if (modalContentRef.current) {
      if (modalContentRef.current.requestFullscreen) {
        modalContentRef.current.requestFullscreen();
      }
    }
  };

  // ------------------------------------------------------------
  // Download image
  // ------------------------------------------------------------
  const handleVnkDownload = () => {
    if (!images[currentIndex]) return;

    const img = images[currentIndex];

    const imageUrl = img.image_url;

    const fileName =
      imageUrl.split("/").pop() || `image-${img.id}.jpg`;

    fetch(imageUrl, {
      mode: "cors",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Download failed");
        }

        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Download error:", err);

        alert("Failed to download image.");
      });
  };

  // ------------------------------------------------------------
  // Zoom
  // ------------------------------------------------------------
  const handleVnkZoom = () => {
    setZoomed((prev) => !prev);
  };

  // ------------------------------------------------------------
  // Keyboard navigation
  // ------------------------------------------------------------
  useEffect(() => {
    if (!modalOpen) return;

    const handleVnkKey = (e) => {
      if (e.key === "ArrowLeft") {
        showVnkPrev(e);
      }

      if (e.key === "ArrowRight") {
        showVnkNext(e);
      }

      if (e.key === "Escape") {
        closeVnkModal();
      }
    };

    window.addEventListener("keydown", handleVnkKey);

    return () => {
      window.removeEventListener("keydown", handleVnkKey);
    };
  }, [modalOpen, images.length]);

  // ------------------------------------------------------------
  // Swipe navigation
  // ------------------------------------------------------------
  useEffect(() => {
    if (!modalOpen) return;

    let startX = 0;

    const handleVnkTouchStart = (e) => {
      startX = e.touches[0].clientX;
    };

    const handleVnkTouchEnd = (e) => {
      const endX = e.changedTouches[0].clientX;

      if (startX - endX > 50) {
        showVnkNext(e);
      }

      if (endX - startX > 50) {
        showVnkPrev(e);
      }
    };

    const modalBody = document.querySelector(".modal-body");

    if (modalBody) {
      modalBody.addEventListener(
        "touchstart",
        handleVnkTouchStart
      );

      modalBody.addEventListener(
        "touchend",
        handleVnkTouchEnd
      );

      return () => {
        modalBody.removeEventListener(
          "touchstart",
          handleVnkTouchStart
        );

        modalBody.removeEventListener(
          "touchend",
          handleVnkTouchEnd
        );
      };
    }
  }, [modalOpen, images.length]);

  // ------------------------------------------------------------
  // Pan image when zoomed
  // ------------------------------------------------------------
  const handleVnkPan = (e) => {
    if (!zoomed || !modalImageRef.current) return;

    const rect =
      modalImageRef.current.getBoundingClientRect();

    let x;
    let y;

    if (e.type === "mousemove") {
      x = e.clientX;
      y = e.clientY;
    } else if (e.type === "touchmove") {
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    } else {
      return;
    }

    const xPercent =
      ((x - rect.left) / rect.width) * 100;

    const yPercent =
      ((y - rect.top) / rect.height) * 100;

    modalImageRef.current.style.transformOrigin =
      `${xPercent}% ${yPercent}%`;
  };

  // ------------------------------------------------------------
  // Double tap / double click
  // ------------------------------------------------------------
  const lastTapRef = useRef(0);

  const handleVnkDoubleTap = () => {
    const now = Date.now();

    if (now - lastTapRef.current < 300) {
      handleVnkZoom();
    }

    lastTapRef.current = now;
  };

  // ------------------------------------------------------------
  // Current category title
  // ------------------------------------------------------------
  const currentCategory =
    categoriesList.find(
      (category) =>
        String(category.key) === String(selectedCategory)
    )?.label || "All";

  // ------------------------------------------------------------
  // JSX
  // ------------------------------------------------------------
  return (
    <div className="vnk-gallery-page">

      {/* ======================================================
          Category Dropdown
      ====================================================== */}

      <div className="vnk-gallery-dropdown-wrapper">

        <h2 className="vnk-gallery-heading">
          {currentCategory}
        </h2>

        <div className="vnk-gallery-dropdown-container">

          {/* Dropdown Trigger */}
          <div
            className="vnk-gallery-dropdown-trigger"
            onClick={() =>
              setDropdownOpen((prev) => !prev)
            }
          >
            <span>
              {currentCategory}
            </span>

            <i
              className={`fas fa-chevron-${
                dropdownOpen ? "up" : "down"
              }`}
            ></i>
          </div>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="vnk-gallery-dropdown-menu">

              {categoriesList.map((cat) => (
                <div
                  key={cat.key}
                  className={`vnk-gallery-dropdown-item${
                    String(selectedCategory) ===
                    String(cat.key)
                      ? " active"
                      : ""
                  }`}
                  onClick={() => {
                    setSelectedCategory(cat.key);
                    setDropdownOpen(false);
                  }}
                >
                  {cat.label}
                </div>
              ))}

            </div>
          )}

        </div>
      </div>

      {/* ======================================================
          Image Grid
      ====================================================== */}

      <div className="vnk-gallery-grid">

        {loading ? (

          <p>Loading gallery...</p>

        ) : error ? (

          <p>{error}</p>

        ) : images.length > 0 ? (

          images.map((img, idx) => (

            <div
              key={img.id || idx}
              className="vnk-gallery-item"
              onClick={() => openVnkModal(idx)}
            >

              <img
                src={img.image_url}
                alt={img.galleryName}
                className="vnk-gallery-thumb"
              />

            </div>

          ))

        ) : (

          <p>No images found.</p>

        )}

      </div>

      {/* ======================================================
          Image Modal
      ====================================================== */}

      {modalOpen && images.length > 0 && (

        <div
          className="modal"
          style={{ display: "block" }}
          tabIndex={-1}
        >

          <div
            className="modal-dialog modal-dialog-centered vnk-modal-dialog-wrap m-0"
          >

            <div
              className={`modal-content vnk-modal-content-wrap${
                zoomed ? " vnk-zoomed-in" : ""
              }`}
              ref={modalContentRef}
            >

              {/* ------------------------------------------------
                  Modal Header
              ------------------------------------------------ */}

              <div
                className="modal-header"
                style={{ borderBottom: "none" }}
              >

                <h5 className="modal-title">

                  <span id="currentImage">
                    {currentIndex + 1}
                  </span>

                  {" / "}

                  <span id="totalImages">
                    {images.length}
                  </span>

                </h5>

                <div className="vnk-modal-header-buttons">

                  {/* Fullscreen */}
                  <button
                    type="button"
                    className="btn"
                    title="Fullscreen"
                    onClick={handleVnkFullscreen}
                  >
                    <i className="fas fa-expand"></i>
                  </button>

                  {/* Zoom */}
                  <button
                    type="button"
                    className="btn"
                    title="Zoom"
                    onClick={handleVnkZoom}
                  >
                    <i
                      className={`fas ${
                        zoomed
                          ? "fa-search-minus"
                          : "fa-search-plus"
                      }`}
                    ></i>
                  </button>

                  {/* Download */}
                  <button
                    type="button"
                    className="vnk-share-btn"
                    title="Download"
                    onClick={handleVnkDownload}
                  >
                    <i className="fas fa-download"></i>
                  </button>

                  {/* Close */}
                  <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    onClick={closeVnkModal}
                  >
                    <span>&times;</span>
                  </button>

                </div>
              </div>

              {/* ------------------------------------------------
                  Modal Body
              ------------------------------------------------ */}

              <div
                className="modal-body text-center"
                onMouseMove={handleVnkPan}
                onTouchMove={handleVnkPan}
                onDoubleClick={handleVnkZoom}
                onTouchEnd={handleVnkDoubleTap}
              >

                <img
                  ref={modalImageRef}
                  id="modalImage"
                  src={images[currentIndex].image_url}
                  alt={images[currentIndex].galleryName}
                  className={`img-fluid${
                    zoomed ? " vnk-zoomed-in" : ""
                  }`}
                  style={
                    zoomed
                      ? {
                          transform: "scale(2)",
                          cursor: "move",
                        }
                      : {
                          transform: "",
                          cursor: "pointer",
                        }
                  }
                  onClick={handleVnkZoom}
                />

                {/* Previous */}
                <button
                  type="button"
                  className="prev"
                  id="prevImage"
                  onClick={showVnkPrev}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>

                {/* Next */}
                <button
                  type="button"
                  className="next"
                  id="nextImage"
                  onClick={showVnkNext}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>

              </div>

              {/* ------------------------------------------------
                  Modal Footer
              ------------------------------------------------ */}

              <div
                className="modal-footer"
                style={{ borderTop: "none" }}
              >

                <div className="vnk-modal-caption">
                  {images[currentIndex].galleryName}
                </div>

              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default VnkGallery;